<script setup>
import { ref, onMounted, onBeforeUnmount, useSlots } from 'vue';
import { useOverlayStack } from '../../composables/useOverlayStack.js';
import { provideUnsavedChanges } from '../../composables/useUnsavedChanges.js';

const onScreen = ref(false);
const transitioning = ref(true);
const emit = defineEmits(['self-close-prevented', 'close']);
const slots = useSlots()
const {
    backdropZIndex,
    isTopOverlay,
    panelZIndex,
    registerOverlay,
    unregisterOverlay,
} = useOverlayStack('slideout-with-slots')
const { hasUnsavedChanges } = provideUnsavedChanges()
const pendingClose = ref(false)

const props = defineProps({
    title: {
        type: String,
        default: null,
        required: false,
    },
    allowSelfClose: {
        type: Boolean,
        default: true,
        required: false,
    },
    maxWidthClass: {
        type: String,
        default: 'max-w-lg',
        required: false,
    },
    resizeable: {
        type: Boolean,
        default: false,
        required: false,
    },
    headerStyle: {
        type: String,
        default: 'default',
        required: false,
    },
    // Ask before closing. Forms inside the slideout can also report this via useUnsavedChanges().
    isDirty: {
        type: Boolean,
        default: false,
        required: false,
    }
})

const showTitleSlot = () => {
    return !!slots.title
}

const _maxWidthClass = ref(props.maxWidthClass)

const closeFromTopOverlayInteraction = () => {
    if (!isTopOverlay.value) {
        return;
    }

    close();
}

const handleKeydown = (e) => {
    if (e.key !== 'Escape') {
        return;
    }
    // Escape dismisses the unsaved-changes prompt rather than confirming it
    if (pendingClose.value) {
        pendingClose.value = false;
        return;
    }
    closeFromTopOverlayInteraction();
}

onMounted(() => {
    registerOverlay();
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', handleKeydown);

    setTimeout(function(){
        onScreen.value = true;
        transitioning.value = false;
    }, 1);
})

// force skips the unsaved-changes prompt, e.g. when the edited record was just deleted.
const close = ({ force = false } = {}) => {
    if (!props.allowSelfClose) {
        emit('self-close-prevented');
        return;
    }
    if (!force && !pendingClose.value && (props.isDirty || hasUnsavedChanges())) {
        pendingClose.value = true;
        return;
    }
    pendingClose.value = false;
    transitioning.value = true;
    setTimeout(function(){
        onScreen.value = false;
        unregisterOverlay();
        emit('close');
    }, 150);
}

const toggleMaxWidth = () => {
    if (_maxWidthClass.value === props.maxWidthClass ) {
        _maxWidthClass.value = 'max-w-lg'
    } else {
        _maxWidthClass.value = props.maxWidthClass
    }
}

onBeforeUnmount(() => {
    onScreen.value = false;
    unregisterOverlay();
    document.removeEventListener('keydown', handleKeydown);
    document.body.classList.remove('overflow-hidden');
})

defineExpose({
    close
})

</script>
<template>
    <Teleport to="body">
        <div
            v-if="onScreen"
            :style="{ zIndex: backdropZIndex }"
            class="fixed inset-0 transition-colors"
            :class="transitioning ? 'bg-transparent' : 'bg-black/50'"
            @click="closeFromTopOverlayInteraction"
        ></div>
        <div
            :style="{ zIndex: panelZIndex }"
            class="fixed top-0 right-0 bottom-0 w-screen bg-white shadow-lg transition-all"
            :class="[transitioning ? 'translate-x-full' : 'translate-x-0', _maxWidthClass]"
        >
            <div class="flex max-h-screen flex-col">
                <div>
                    <div
                        class="flex w-full items-start justify-between"
                        :class="headerStyle === 'default' ? 'p-4 text-gray-800 border-b' : 'pt-2 border-b px-6'"
                    >
                        <div>
                            <div
                                v-if="resizeable && props.maxWidthClass !== 'max-w-lg'"
                                class="hidden relative xl:block"
                            >
                                <span
                                    class="absolute right-full bottom-full translate-y-1 -translate-x-1/3 opacity-80"
                                >
                                    <button-secondary
                                        size="sm"
                                        @click="toggleMaxWidth"
                                    >
                                        <i
                                            class="fa-regular fa-chevron-left transform transition-transform"
                                            :class="_maxWidthClass !== 'max-w-lg' ? 'rotate-180' : ''"
                                        ></i>
                                    </button-secondary>
                                </span>
                            </div>
                            <h1
                                v-if="props.title && props.headerStyle !== 'minimal'"
                            >
                                {{ props.title }}
                            </h1>
                            <slot name="title">
                            </slot>
                        </div>
                        <div
                            v-if="!slots.actions"
                            class="ml-3 flex h-7 items-center">
                            <button
                                type="button"
                                class="mt-2 rounded-md text-gray-800 hover:text-gray-600 focus:outline-none"
                                tabindex="0"
                                @click="close()"
                            >
                                <span class="sr-only">{{ _mft('elements:close') }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true" class="h-6 w-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        <slot name="actions"></slot>
                    </div>
                </div>
                <div class="min-h-screen overflow-y-scroll p-4 lg:p-6">
                    <slot name="body"></slot>
                    <div class="h-48"></div>
                </div>
            </div>
        </div>
        <!-- Outside the panel: its transform would otherwise center this within the panel instead of the screen -->
        <div
            v-if="pendingClose"
            :style="{ zIndex: panelZIndex + 1 }"
            class="fixed inset-0 flex items-center justify-center p-4 bg-black/20"
            @click.self="pendingClose = false"
        >
            <div class="w-full max-w-md rounded-xl bg-white shadow-xl p-4 lg:p-8">
                <p class="text-gray-800">{{ _mft('modal:basic.changesWillNotBeSaved') }}</p>
                <div class="mt-4 flex justify-end space-x-2">
                    <button-secondary @click="pendingClose = false">{{ _mft('shared:confirm.cancel') }}</button-secondary>
                    <button-base style-type="danger" @click="close()">{{ _mft('shared:confirm.positive') }}</button-base>
                </div>
            </div>
        </div>
    </Teleport>
</template>
<style>
.almost-full-screen {
    width: 90vw;
    max-width: 1280px;
}
</style>
